import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Client} from "@stomp/stompjs";
import {useUser} from "../providers/user-context";
import {config} from "../config/environment";
import {fetchUnreadCount, markNotificationRead, markAllNotificationsRead} from "./notification-api";
import NotificationFeedback from "./notification-feedback";
import useNotificationSound from "./use-notification-sound";
import coalescedRequest from "./coalesced-request";

const NotificationContext = createContext(null);
export const useNotifications = () => useContext(NotificationContext);
export const notificationIdentity = n => n.notificationId || [n.notificationType, n.orderNumber, n.notificationDate].join(":");

export function NotificationProvider({children}) {
    const user = useUser();
    const identity = [user.tenantId, user.userFullName, user.tenantType].join(":");
    const resolved = useRef({});
    if (!user.userLoading) resolved.current = {identity, role: user.userRole};
    // A language change re-fetches the same user's role. Keep the current page mounted during that refresh.
    const role = user.userLoading && resolved.current.identity === identity ? resolved.current.role : user.userRole;
    const enabled = Boolean(user.tenantId && !user.userError &&
        (user.tenantType === "PHARMACY" || (user.tenantType === "IMPORTER" && ["ADMIN", "OPERATOR", "SYSTEM"].includes(role))));
    // Identity changes discard subscriptions, pending work and cached read state together.
    const scope = [identity, role, enabled].join(":");
    return <NotificationSession key={scope} scope={scope} tenantId={user.tenantId} tenantType={user.tenantType} enabled={enabled}
        soundPreferenceKey={`web-notification-sound:${identity}`}>
        {children}
    </NotificationSession>;
}

function NotificationSession({children, scope, tenantId, tenantType, enabled, soundPreferenceKey}) {
    const {i18n} = useTranslation(), navigate = useNavigate();
    const language = i18n.language;
    const {soundEnabled, toggleSound, playSound} = useNotificationSound(soundPreferenceKey, enabled);
    const [count, setCount] = useState(null), [countError, setCountError] = useState(false);
    const [connected, setConnected] = useState(null), [revision, setRevision] = useState(0);
    const [pendingIds, setPendingIds] = useState(new Set()), [markingAll, setMarkingAll] = useState(false);
    const [readIds, setReadIds] = useState(new Set()), [feedback, setFeedback] = useState(null), [incoming, setIncoming] = useState(null);
    const [feedbackContainer, setFeedbackContainer] = useState(null);
    useEffect(() => {
        // A hidden transient banner must not reappear minutes later after a persistent retry error is dismissed.
        if (feedback && incoming) {
            setIncoming(null);
        }
    }, [feedback, incoming]);
    const alive = useRef(false), generation = useRef(0), countRequest = useRef(null), currentLanguage = useRef(language);
    currentLanguage.current = language;
    const pending = useRef(new Set()), allPending = useRef(false), seen = useRef(new Set());
    const observe = useCallback(rows => {
        rows.forEach(row => seen.current.add(notificationIdentity(row)));
        while (seen.current.size > 1000) seen.current.delete(seen.current.values().next().value);
    }, []);
    const refreshCount = useCallback(() => countRequest.current?.run(), []);

    useEffect(() => {
        alive.current = true;
        generation.current++;
        if (!enabled) return () => {alive.current = false; generation.current++;};
        const request = coalescedRequest(async signal => {
            try {
                const value = await fetchUnreadCount(currentLanguage.current, signal);
                if (!signal.aborted) {setCount(value); setCountError(false);}
            } catch {
                if (!signal.aborted) setCountError(true);
            }
        });
        countRequest.current = request;
        // Mutation lifetime follows the authenticated session, independently of language/subscriptions.
        return () => {alive.current = false; generation.current++; request.dispose(); countRequest.current = null;};
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return;
        let active = true;
        const current = () => active;
        const refresh = () => {
            if (!current() || document.visibilityState === "hidden") return;
            refreshCount();
            setRevision(value => value + 1);
        };
        // Feeds perform their own initial fetch; only the shared count needs loading here.
        if (document.visibilityState !== "hidden") refreshCount();
        // One connection serves the bell, panel and history. REST remains authoritative for counts.
        const client = new Client({brokerURL: `${config.WEBSOCKET_URL}/ws`, reconnectDelay: 5000,
            heartbeatIncoming: 10000, heartbeatOutgoing: 10000, connectionTimeout: 10000,
            onConnect: () => {
                if (!current()) return;
                setConnected(true); refresh();
                client.subscribe(`/queue/notifications/${tenantId}`, message => {
                    if (!current()) return;
                    try {
                        const row = JSON.parse(message.body);
                        if (!row || row.recipientUserId || row.tenantId !== tenantId) return;
                        const key = notificationIdentity(row);
                        if (seen.current.has(key)) return;
                        observe([row]);
                        if (!row.read) playSound();
                        if (!row.read && document.visibilityState !== "hidden") setIncoming(row);
                        refresh();
                    } catch { /* Malformed events do not replace the persisted inbox. */ }
                });
            },
            onDisconnect: () => {if (current()) setConnected(false);},
            onStompError: () => {if (current()) setConnected(false);},
            onWebSocketClose: () => {if (current()) setConnected(false);},
            onWebSocketError: () => {if (current()) setConnected(false);},
        });
        client.activate();
        const timer = setInterval(refresh, 15000);
        document.addEventListener("visibilitychange", refresh);
        window.addEventListener("online", refresh);
        return () => {active = false; clearInterval(timer);
            document.removeEventListener("visibilitychange", refresh); window.removeEventListener("online", refresh); client.deactivate();};
    }, [enabled, tenantId, refreshCount, observe, playSound]);

    const markRead = useCallback(async row => {
        const id = row.notificationId;
        if (!enabled || !id || row.read || readIds.has(id) || pending.current.has(id) || allPending.current) return;
        const epoch = generation.current;
        pending.current.add(id); setPendingIds(new Set(pending.current)); setFeedback(null);
        try {
            await markNotificationRead(id, currentLanguage.current);
            if (!alive.current || epoch !== generation.current) return;
            setReadIds(previous => new Set(previous).add(id)); setRevision(value => value + 1); refreshCount();
        } catch (error) {
            if (alive.current && epoch === generation.current) setFeedback({message: error.response?.data?.message || "NotificationReadFailed", retry: () => markRead(row)});
        } finally {
            pending.current.delete(id);
            if (alive.current) setPendingIds(new Set(pending.current));
        }
    }, [enabled, readIds, refreshCount]);

    const markAll = useCallback(async () => {
        if (!enabled || allPending.current || pending.current.size) return;
        const epoch = generation.current;
        allPending.current = true; setMarkingAll(true); setFeedback(null);
        try {
            await markAllNotificationsRead(currentLanguage.current);
            if (!alive.current || epoch !== generation.current) return;
            setRevision(value => value + 1); refreshCount();
        } catch (error) {
            if (alive.current && epoch === generation.current) setFeedback({message: error.response?.data?.message || "NotificationReadFailed", retry: markAll});
        } finally {allPending.current = false; if (alive.current) setMarkingAll(false);}
    }, [enabled, refreshCount]);

    const openNotification = useCallback(row => {
        if (!enabled) return;
        // Reading and navigation are independent: a failed read write must not block an order.
        markRead(row); setIncoming(null);
        const prefix = tenantType === "PHARMACY" ? "/pharmacy/my-orders?approval=" : "/distributor/my-orders?approval=";
        if (row.notificationType === "ORDER_PROPOSAL_DECISION" && row.link?.startsWith(prefix)
            && /^[0-9a-f-]{36}$/i.test(row.link.slice(prefix.length))) {
            navigate(row.link); return;
        }
        const shipmentsPrefix = tenantType === "PHARMACY" ? "/pharmacy/my-orders?shipments=" : "/distributor/my-orders?shipments=";
        if (row.notificationType === "ORDER_SHIPMENT_UPDATE" && row.link?.startsWith(shipmentsPrefix)
            && /^[0-9a-f-]{36}$/i.test(row.link.slice(shipmentsPrefix.length))) {
            navigate(row.link); return;
        }
        if (row.orderNumber) navigate(tenantType === "PHARMACY" ? "/pharmacy/my-orders" : "/distributor/my-orders", {
            state: {notificationOrder: {orderNumber: row.orderNumber, ownerId: row.notificationOwnerId, ownerName: row.title}},
        });
    }, [enabled, markRead, navigate, tenantType]);
    const dismissIncoming = useCallback(id => setIncoming(current => {
        // A timeout belonging to a replaced banner must not dismiss the newer event.
        if (id && current && notificationIdentity(current) !== id) {
            return current;
        }
        return null;
    }), []);
    const value = {scope, tenantId, enabled, count, countError, connected, revision, readIds, pendingIds, markingAll, soundEnabled, toggleSound,
        markRead, markAll, openNotification, observe, refreshCount, feedback, incoming,
        feedbackContainer, setFeedbackContainer,
        dismissFeedback: () => setFeedback(null), dismissIncoming};
    return <NotificationContext.Provider value={value}>{children}<NotificationFeedback/></NotificationContext.Provider>;
}
